import { useState } from "react";
import { useSelector } from "react-redux";
import { Button, Tab, Tabs, ListGroup, Spinner, Form } from "react-bootstrap";
import { useGetDeliveryPartnerOrdersQuery, useUpdateOrderDeliveryMutation, useUpdateOrderCollectionMutation } from "../../slices/ordersApiSlice";
import { useVerifyOTPMutation } from "../../slices/smsApiSlice";
import { useSendWhatsAppOTPMutation } from "../../slices/whatsAppApiSlice";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import { toast } from "react-toastify";
import { dateFormatterShortWeekDay, dateFormatter } from "../../utils/dateHelper";

const DeliveryPartnerHomeScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("pending");

  const [otpStates, setOtpStates] = useState({});

  const { data: pendingOrders, isLoading: isLoadingPendingOrders, error: errorPendingOrders, refetch: refetchPendingOrders } = useGetDeliveryPartnerOrdersQuery("pending");
  const { data: deliveredOrders, isLoading: isLoadingDeliveredOrders, error: errorDeliveredOrders, refetch: refetchDeliveredOrders } = useGetDeliveryPartnerOrdersQuery("delivered");
  const [updateOrderCollection, { isLoading: isLoadingupdateOrderCollection }] = useUpdateOrderCollectionMutation();
  const [updateOrderDelivery, { isLoading: isLoadingupdateOrderDelivery }] = useUpdateOrderDeliveryMutation();
  //const [sendOTP, { isLoading: isLoadingSendOTP }] = useSendOTPMutation();
  const [sendOTP, { isLoading: isLoadingSendOTP }] = useSendWhatsAppOTPMutation();
  const [verifyOTP, { isLoading: isLoadingVerifyOTP }] = useVerifyOTPMutation();

  if (!userInfo) {
    return <p>Please log in</p>;
  }

  const { name, deliveryPartner } = userInfo;

  if (!deliveryPartner?.isApproved) {
    return (
      <div className="p-3 border rounded">
        <h2>Approval Pending</h2>
        <p>
          Hi {name}, your delivery partner registration has been received.  
          Our team is reviewing your details. You will be notified once approved.
        </p>
      </div>
    );
  }

  const handleUpdateOrderCollection = async (orderId) => {
    try {
      await updateOrderCollection(orderId).unwrap();
      await refetchPendingOrders();

      toast.success("Order is collected", { autoClose: 1000 });
    } catch (err) {
      console.error(err);
      toast.error("Could not collect order", { autoClose: 2000 });
    }
  };

  const handleSendOTP = async (orderId) => {
    try {
      await sendOTP({ orderId }).unwrap();
      setOtpStates((prev) => ({
        ...prev,
        [orderId]: { step: "otpSent", otp: "" }
      }));
      toast.success("OTP sent to customer", { autoClose: 1000 });
    } catch (err) {
      console.error(err);
      toast.error("Failed to send OTP", { autoClose: 2000 });
    }
  };

  const handleVerifyOTPAndUpdateOrderDelivery = async (orderId) => {
    const enteredOtp = otpStates[orderId]?.otp;
    let otpVerified = true;
    if (!enteredOtp) {
      toast.error("Please enter OTP", { autoClose: 2000 });
      return;
    }

    try {
      await verifyOTP({ orderId, otp:enteredOtp }).unwrap();
    } catch (err) {
      toast.error("OTP verification failed", {autoClose: 2000});
      otpVerified = false;
    }
    
    if (otpVerified) {
      try {
        await updateOrderDelivery(orderId).unwrap();
        await refetchPendingOrders();
        await refetchDeliveredOrders();

        setOtpStates((prev) => {
          const newState = { ...prev };
          delete newState[orderId];
          return newState;
        })

        toast.success("Order is delivered", { autoClose: 1000 });
      } catch (err) {
        console.error(err);
        toast.error("Could not set order as delivered", { autoClose: 2000 });
      }
    }
  };

  const renderOrders = (order) => {
    const otpState = otpStates[order._id];
    return (
      <ListGroup.Item key={order._id} className="mb-3">
        <p><strong>Order Id:</strong> {order._id}</p>
        <p><strong>Sourcing Owner:</strong> {order.sourcing?.sourcingPartner?.name} ({order.sourcing?.sourcingPartner?.phone})</p>
        <p>
          <strong>Pick Up Address:</strong><br />
          {order.sourcing?.sourcingPartner?.sourcingPartner?.organizationName}<br/>
          {order.sourcing?.sourcingPartnerAddress?.addressLine1}{order.sourcing?.sourcingPartnerAddress?.addressLine2 && <br/>}
          {order.sourcing?.sourcingPartnerAddress?.addressLine2}<br/>
          {order.sourcing?.sourcingPartnerAddress?.city}, {order.sourcing?.sourcingPartnerAddress?.state} - {order.sourcing?.sourcingPartnerAddress?.postalCode}<br/>
          {order.sourcing?.sourcingPartnerAddress?.phone}, {order.sourcing?.sourcingPartnerAddress?.alternatePhones}<br/>
          {order.sourcing?.sourcingPartnerAddress?.googleMapsLocationUrl && (
            <a
              href={order.sourcing.sourcingPartnerAddress.googleMapsLocationUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              📍 View Location
            </a>
          )}
        </p>

        <p><strong>Customer:</strong> {order.user?.name} ({order.user?.phone})</p>
        <p>
          <strong>Delivery Address:</strong><br />
          {order.shippingAddress?.name}<br/>
          {order.shippingAddress?.addressLine1}{order.shippingAddress?.addressLine2 && <br/>}
          {order.shippingAddress?.addressLine2}<br/>
          {order.shippingAddress?.city}, {order.shippingAddress?.state} - {order.shippingAddress?.postalCode}<br/>
          {order.shippingAddress?.phone}
        </p>
        <p>
          <strong>Preferred Delivery Time Slot:</strong><br/>
          {dateFormatterShortWeekDay(order.delivery?.preferredDeliveryDate)} | {order.delivery?.preferredDeliveryTimeSlot}
        </p>
        {order.delivery.isDelivered && (
          <p>
            <strong>Delivered At:</strong><br/>
            {dateFormatter(order.delivery?.deliveredAt)}
          </p>
        )}
        <p><strong>Items:</strong></p>
        <ul>
          {order.orderItems.map((item) => (
            <li key={item._id}>
              {item.name} ({item.weight})
              {item.spiceLevel !== "N/A" && (` | Spice Level: ${item.spiceLevel} | Qty: ${item.quantity}`)}
              {item.sweetness !== "N/A" && (` | Sweetness: ${item.sweetness} | Qty: ${item.quantity}`)}
            </li>
          ))}
        </ul>

        {!order.sourcing.isCollected && !order.delivery.isDelivered && (
          <>
            <Button
              variant="info"
              className="mt-2"
              onClick={() => handleUpdateOrderCollection(order._id)}
              disabled={isLoadingupdateOrderCollection}
              >
              {isLoadingupdateOrderCollection ? (
                <>
                  <Spinner animation="border" size="sm" /> Updating...
                </>
              ) : (
                "Collect Order"
              )}
            </Button>
          </>
        )}

        {/* OTP / Delivery flow */}
        {order.sourcing.isCollected && !order.delivery.isDelivered && (
          <>
            {otpState?.step === "otpSent" ? (
              <>
                <Form.Control
                  type="text"
                  placeholder="Enter OTP"
                  className="mt-3"
                  value={otpState.otp}
                  onChange={(e) =>
                    setOtpStates((prev) => ({
                      ...prev,
                      [order._id]: { ...otpState, otp: e.target.value }
                    }))
                  }
                />
                <Button
                  variant="success"
                  className="mt-2"
                  onClick={() => handleVerifyOTPAndUpdateOrderDelivery(order._id)}
                  disabled={isLoadingVerifyOTP || isLoadingupdateOrderDelivery}
                >
                  {isLoadingVerifyOTP || isLoadingupdateOrderDelivery ? (
                    <>
                      <Spinner animation="border" size="sm" /> Verifying...
                    </>
                  ) : (
                    "Verify OTP and Deliver"
                  )}
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                className="mt-3"
                onClick={() => handleSendOTP(order._id)}
                disabled={isLoadingSendOTP}
              >
                {isLoadingSendOTP ? (
                  <>
                    <Spinner animation="border" size="sm" /> Sending OTP...
                  </>
                ) : (
                  "Get Delivery OTP"
                )}
              </Button>
            )}
          </>
        )}
        <hr/>
      </ListGroup.Item>
    );
  };

  return (
    <div className="p-3">
      <h2>Hi {name}</h2>
      <p>Here are your assigned deliveries</p>

      {isLoadingPendingOrders || isLoadingDeliveredOrders ? (
        <Loader />
      ) : errorDeliveredOrders ? (
        <Message variant="danger">{errorDeliveredOrders?.data?.message || errorDeliveredOrders.error}</Message>
      ) : errorPendingOrders ? (
        <Message variant="danger">{errorPendingOrders?.data?.message || errorPendingOrders.error}</Message>
      ) : (
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3 delivery-tabs">
          <Tab variant="info" eventKey="pending" title="Pending Deliveries">
            <div className="p-2 mt-3 border rounded">
              {pendingOrders?.length > 0 ? (
                pendingOrders.map((order) => renderOrders(order))
              ) : (
                <Message>No pending orders</Message>
              )}
            </div>
          </Tab>

          <Tab variant="success" eventKey="completed" title="Completed Deliveries">
            <div className="p-2 mt-3 border rounded">
              {deliveredOrders?.length > 0 ? (
                deliveredOrders.map((order) => renderOrders(order))
              ) : (
                <Message>No delivered orders</Message>
              )}
            </div>
          </Tab>
        </Tabs>
      )}
    </div>
  );
};

export default DeliveryPartnerHomeScreen;